// frontend/src/features/agencyAccounts/agencyAccountsSlice.js
// This Redux slice manages agency accounts state—fetching, creating, and deleting accounts via UPS Admin API endpoints, with loading/error handling and list updates.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Fetch accounts (optionally filtered)
export const fetchAccounts = createAsyncThunk(
  'agencyAccounts/fetchAll',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters.agencyId) params.set('agencyId', filters.agencyId);
      if (filters.role)     params.set('role',     filters.role);
      if (filters.q)        params.set('q',        filters.q);
      const qs = params.toString();
      const { data } = await axios.get(`/ups-admin/accounts${qs ? `?${qs}` : ''}`);
      return { data, filters };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load accounts');
    }
  }
);

// Create a new agency account
export const createAccount = createAsyncThunk(
  'agencyAccounts/create',
  async (accountData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/ups-admin/agencies/account', accountData);
      return data; // should include { id, username, email, role, agencyId|agency_id, ... }
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to create account');
    }
  }
);

// Delete an agency account
export const deleteAccount = createAsyncThunk(
  'agencyAccounts/delete',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`/ups-admin/accounts/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to delete account');
    }
  }
);

const agencyAccountsSlice = createSlice({
  name: 'agencyAccounts',
  initialState: {
    list: [],
    loading: false,
    error: null,
    lastQuery: {}, // { agencyId?, role?, q? }
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchAccounts.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.data;
        state.lastQuery = action.payload.filters || {};
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createAccount.pending, state => {
        state.error = null;
      })
      .addCase(createAccount.fulfilled, (state, action) => {
        const acc = action.payload || {};
        const accAgencyId = acc.agencyId ?? acc.agency_id; // handle snake_case or camelCase
        const { agencyId, role } = state.lastQuery || {};

        // Only show new account in the *current* view:
        const matchesAgency = !agencyId || String(agencyId) === String(accAgencyId ?? '');
        const matchesRole   = !role     || String(role)     === String(acc.role ?? '');

        if (matchesAgency && matchesRole) {
          state.list.unshift(acc);
        }
        // If you want it to always appear on the global page later,
        // a fresh fetchAccounts() will hydrate it anyway.
      })
      .addCase(createAccount.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(deleteAccount.fulfilled, (state, action) => {
        state.list = state.list.filter(acc => acc.id !== action.payload);
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export default agencyAccountsSlice.reducer;